import { useState, useEffect, useMemo } from 'react';
import { getOrdersTagWise } from '@/services/reportsService';
import { toastMessage } from '@/utils/toast-message';
import { Tag, RefreshCw, Info, ShoppingCart, Banknote, Hash } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface TagWiseData {
  tag: string;
  order_count: number;
  total_revenue: number;
  paid_revenue: number;
  paid_orders: number;
}

const PALETTE = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f97316',
  '#eab308', '#22c55e', '#14b8a6', '#3b82f6',
  '#ef4444', '#a855f7',
];

const fmt = (v: number) => `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const TagWiseReport = () => {
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<TagWiseData[]>([]);
  const [chartMode, setChartMode] = useState<'orders' | 'revenue'>('orders');

  const fetchReport = async () => {
    if (!fromDate || !toDate) { toastMessage('Please select both dates', 'error'); return; }
    setLoading(true);
    try {
      const res = await getOrdersTagWise({ from_date: fromDate, to_date: toDate });
      if (res.success) setData(res.data as TagWiseData[]);
      else toastMessage('Failed to fetch report', 'error');
    } catch (e: any) {
      toastMessage(e.message || 'Error', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, []);

  const sorted = useMemo(() =>
    [...data].sort((a, b) => chartMode === 'orders'
      ? b.order_count - a.order_count
      : b.total_revenue - a.total_revenue),
    [data, chartMode]
  );

  const top10 = sorted.slice(0, 10);

  const chartData = useMemo(() => ({
    labels: top10.map(r => r.tag.length > 30 ? r.tag.slice(0, 28) + '…' : r.tag),
    datasets: [{
      label: chartMode === 'orders' ? 'Orders' : 'Revenue (₹)',
      data: top10.map(r => chartMode === 'orders' ? r.order_count : Math.round(r.total_revenue)),
      backgroundColor: PALETTE,
      borderRadius: 4,
    }],
  }), [top10, chartMode]);

  const kpis = useMemo(() => ({
    totalTags: data.length,
    totalOrders: data.reduce((s, r) => s + r.order_count, 0),
    totalRevenue: data.reduce((s, r) => s + r.total_revenue, 0),
  }), [data]);

  return (
    <div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-950">

      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-brandColor rounded-lg text-white">
              <Tag className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Orders by Tag</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Order volume & revenue per managed CRM tag</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
              className="px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
              className="px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
            <button onClick={fetchReport} disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-brandColor text-white text-sm rounded-lg hover:bg-brandColor/90 disabled:opacity-50 font-medium transition-all">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-5">

        {/* Explanation */}
        <div className="flex gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
          <Info className="h-4 w-4 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
            <p className="font-semibold">About this report</p>
            <p className="text-blue-700 dark:text-blue-400 leading-relaxed">
              Shows order volume and total revenue for each <strong>managed CRM tag</strong> in your account.
              Tags are attached to Shopify orders to track which team or campaign drove the sale.
              Use this report to compare tag-level performance, identify top-producing teams, and allocate resources accordingly.
            </p>
          </div>
        </div>

        {/* KPI cards */}
        {data.length > 0 && (
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-xl p-4 text-white shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium opacity-80">Active Tags</p>
                <Hash className="h-4 w-4 opacity-70" />
              </div>
              <p className="text-2xl font-bold">{kpis.totalTags}</p>
            </div>
            <div className="bg-gradient-to-br from-violet-500 to-violet-700 rounded-xl p-4 text-white shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium opacity-80">Total Orders</p>
                <ShoppingCart className="h-4 w-4 opacity-70" />
              </div>
              <p className="text-2xl font-bold">{kpis.totalOrders.toLocaleString()}</p>
            </div>
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-xl p-4 text-white shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium opacity-80">Total Revenue</p>
                <Banknote className="h-4 w-4 opacity-70" />
              </div>
              <p className="text-2xl font-bold">{fmt(kpis.totalRevenue)}</p>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center h-48">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-brandColor" />
              <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading report…</p>
            </div>
          </div>
        )}

        {!loading && data.length > 0 && (
          <>
            {/* Chart */}
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-gray-800 dark:text-white">
                  Top 10 Tags by {chartMode === 'orders' ? 'Order Volume' : 'Revenue'}
                </h2>
                <div className="flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
                  <button onClick={() => setChartMode('orders')}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${chartMode === 'orders' ? 'bg-brandColor text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                    Orders
                  </button>
                  <button onClick={() => setChartMode('revenue')}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${chartMode === 'revenue' ? 'bg-brandColor text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                    Revenue
                  </button>
                </div>
              </div>
              <div style={{ height: Math.max(280, top10.length * 34) }}>
                <Bar
                  data={chartData}
                  options={{
                    indexAxis: 'y',
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: ctx => chartMode === 'revenue'
                            ? ` ${fmt(ctx.parsed.x ?? 0)}`
                            : ` ${ctx.parsed.x ?? 0} orders`,
                        },
                      },
                    },
                    scales: {
                      x: { grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { font: { size: 11 } } },
                      y: { grid: { display: false }, ticks: { font: { size: 11 } } },
                    },
                  }}
                />
              </div>
            </div>

            {/* Full table */}
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-800 dark:text-white">All Tags</h2>
                <span className="text-xs text-gray-400 dark:text-gray-500">{data.length} tags</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="px-5 py-3 text-left text-gray-500 dark:text-gray-400">#</th>
                      <th className="px-5 py-3 text-left text-gray-500 dark:text-gray-400">Tag Name</th>
                      <th className="px-5 py-3 text-right text-gray-500 dark:text-gray-400">Orders</th>
                      <th className="px-5 py-3 text-right text-gray-500 dark:text-gray-400">Total Revenue</th>
                      <th className="px-5 py-3 text-left text-gray-500 dark:text-gray-400 min-w-[160px]">Revenue Share</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {sorted.map((row, idx) => {
                      const revShare = kpis.totalRevenue > 0 ? (row.total_revenue / kpis.totalRevenue) * 100 : 0;
                      return (
                        <tr key={row.tag} className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                          <td className="px-5 py-3.5 text-xs text-gray-400 dark:text-gray-500 font-mono">{idx + 1}</td>
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-2">
                              <div className="h-2 w-2 rounded-full flex-shrink-0"
                                style={{ backgroundColor: PALETTE[idx % PALETTE.length] }} />
                              <span className="font-medium text-gray-900 dark:text-white">{row.tag}</span>
                            </div>
                          </td>
                          <td className="px-5 py-3.5 text-right font-mono text-gray-800 dark:text-gray-200">
                            {row.order_count.toLocaleString()}
                          </td>
                          <td className="px-5 py-3.5 text-right font-medium text-gray-900 dark:text-white">
                            {fmt(row.total_revenue)}
                          </td>
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                                <div className="h-full rounded-full transition-all"
                                  style={{ width: `${revShare}%`, backgroundColor: PALETTE[idx % PALETTE.length] }} />
                              </div>
                              <span className="text-xs text-gray-500 dark:text-gray-400 w-10 text-right">
                                {revShare.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TagWiseReport;
